/**
 * App Component
 * Root application component with routing and state management
 * @component
 */

import React, { useState, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Vendor } from '../types';
import { Header, Footer, SearchBar, Modal } from '../components';
import { VendorGrid } from './VendorGrid';
import { EditorPage } from './EditorPage';
import { PrivacyPage } from './PrivacyPage';
import { TermsPage } from './TermsPage';
import { FAQPage } from './FAQPage';
import { vendors } from '../data/vendors';

const HomePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Memoized filtered vendors based on search term
  const filteredVendors = useMemo(() => {
    if (!searchTerm.trim()) return vendors;

    const term = searchTerm.toLowerCase().trim();
    return vendors.filter(
      (vendor) =>
        vendor.name.toLowerCase().includes(term) ||
        String(vendor.rank).startsWith(term)
    );
  }, [searchTerm]);

  const handleVendorClick = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Delay clearing vendor to allow exit animation
    setTimeout(() => setSelectedVendor(null), 250);
  };

  const handleCTAClick = () => {
    const directorySection = document.getElementById('directory');
    if (directorySection) {
      directorySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Header onCTAClick={handleCTAClick} />

      <main id="directory" className="container mx-auto px-4 py-16">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by name or ranking..."
        />

        <VendorGrid vendors={filteredVendors} onVendorClick={handleVendorClick} />
      </main>

      <Footer year={2025} />

      <Modal
        vendor={selectedVendor}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </>
  );
};

export const App: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/editor" element={<EditorPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/faq" element={<FAQPage />} />
    </Routes>
  );
};
